/* eslint-disable */
import { AGTable, DateRange, Filter, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import numeral from 'numeral';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import NoPermissions from '../../../components/NoPermissions';
import { DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS, DOCUMENT_SEARCH_ALL_ROUTE, DOCUMENT_SEARCH_REPORTS_ROUTE, DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE, NON_SPONSORED, SPONSORS } from '../../../Constants';
import useFetchDocumentSearchData from '../../../hooks/document-search/useFetchDocumentSearchData';
import useFetchDocumentSearchFilters from '../../../hooks/document-search/useFetchDocumentSearchFilters';
import useFetchProfile from '../../../hooks/user/useFetchProfile';

import './DocumentSearch.scss';
import DocumentSearchHighlights from './DocumentSearchHighlights';
import { GridApi, GridReadyEvent, ColumnApi, GetRowIdFunc, ColDef, ColGroupDef, ExcelStyle } from '@ag-grid-community/core';
import { additionalDefaultColDef, COLUMN_PERSISTENCE_KEY, DEFAULT_SORT_ORDER, GRID_PAGE_SIZE } from './DocumentSearch.constants';
import { useQueryClient } from 'react-query';
import { fetchDocumentSearchDataWithoutSearchPhrase } from '../../../services/document-search.service';
import { queryKeys } from '../../../hooks/queryKeyFactory';
import { formatDate } from '../../../utils/date.utils';
import useTableQueryParams from '../../../hooks/table/useTableQueryParams';

function DocumentSearch({ docType, reportType }) {
  const PAGE_SIZE = 25;
  const INITIAL_DATE_RANGE = { startDate: null, endDate: null, relativeRange: null };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { search, pathname } = useLocation();
  const [filterConfig, setFilterConfig] = useState<any>({});
  const [issueDateFilter, setIssueDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const [maturityDateFilter, setMaturityDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  const [publicationDateFilter, setPublicationDateFilter] = useState<any>(INITIAL_DATE_RANGE);
  //DateRange only will reset it's state if resetInput prop changes, so this state makes sure that it's always different
  const [numberOfResets, setNumberOfResets] = useState(0);
  const { data: filters, error: filterError, isLoading: filtersLoading } = useFetchDocumentSearchFilters(docType);
  const [text, setText] = useState('');
  const [searchPhrase, setSearchPhrase] = useState('');
  const [sort, setSort] = useState<any>({ field: null, order: 'desc' });
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchDocumentSearchData(docType, sort, searchPhrase, reportType, filterConfig, publicationDateFilter, issueDateFilter, maturityDateFilter, PAGE_SIZE);

  const { data: profile, isLoading: isProfileLoading, isFetched: isProfileFetched } = useFetchProfile();
  const [invalidate, setInvalidate] = useState(false);
  const [totalResults, setTotalResults] = useState<number>();
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const { onSortChanged } = useTableQueryParams();

  useEffect(() => {
    if (search) {
      try {
        const config = JSON.parse(decodeURI(search.substring(1)));
        setSort(config.sort);
        setFilterConfig(config.filterConfig);
        setIssueDateFilter(config.issueDateFilter);
        setMaturityDateFilter(config.maturityDateFilter);
        setPublicationDateFilter(config.publicationDateFilter);
        setSearchPhrase(config.searchPhrase);
        setText(config.searchPhrase);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    navigate(`${pathname}?${fetchUrlState()}`, {
      replace: true,
    });
  }, [sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter, publicationDateFilter]);

  //to add Non-Sponsored checkbox in Sponsors filter data
  useEffect(() => {
    if (filters && filters.length > 0) {
      filters.find((filter) => {
        if (filter.id === SPONSORS && filter.data.findIndex((sponsorList) => sponsorList.key === NON_SPONSORED) === -1) {
          const nonSponsoredFilter = {
            key: NON_SPONSORED,
            title: NON_SPONSORED,
            showDividerBelowNode: true,
          };
          filter.data.unshift(nonSponsoredFilter);
        }
      });
    }
  }, [filters]);

  const fetchUrlState = () => {
    return `${JSON.stringify({ sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter, publicationDateFilter })}`;
  };

  const updateUrl = () => {
    navigate(`${pathname}?${JSON.stringify({ sort, searchPhrase, filterConfig, issueDateFilter, maturityDateFilter, publicationDateFilter })}`, {
      replace: true,
    });
  };

  const updateSort = (value) => {
    setSort((config) => {
      if (config && config.field && config.field === value) {
        return {
          field: value,
          order: config.order === 'desc' ? 'asc' : 'desc',
        };
      } else {
        return {
          field: value,
          order: 'desc',
        };
      }
    });
  };

  const resetFilters = () => {
    setFilterConfig({});
    setSearchPhrase('');
    setText('');
    setIssueDateFilter({ ...INITIAL_DATE_RANGE });
    setMaturityDateFilter({ ...INITIAL_DATE_RANGE });
    setPublicationDateFilter({ ...INITIAL_DATE_RANGE });
    setNumberOfResets((num) => num + 1);
  };

  const handlePublicationDateChange = (value) => {
    setPublicationDateFilter(value);
  };

  const handleIssueDateChange = (value) => {
    setIssueDateFilter(value);
  };

  const handleMaturityDateChange = (value) => {
    setMaturityDateFilter(value);
  };

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config: any) => {
      config[key] = selected;

      if (selected.length === 0) {
        delete config[key];
      }

      return { ...config };
    });
  };

  const updateSearchPhrase = () => {
    setSearchPhrase(text);
  };

  const renderFilters = () => {
    return (
      <div className="filters-ctx">
        {filters.map((item) => (
          <Filter key={item.id} data={item.data} placeholder={item.name} checkedKeys={filterConfig[item.id] || []} onChange={(checked) => updateFilterConfig(item.id, checked)} />
        ))}
        <DateRange
          label="Publication Date"
          startDate={publicationDateFilter.startDate ? new Date(publicationDateFilter.startDate) : null}
          endDate={publicationDateFilter.endDate ? new Date(publicationDateFilter.endDate) : null}
          relativeRange={publicationDateFilter.relativeRange ? publicationDateFilter.relativeRange : null}
          resetInput={numberOfResets}
          error={false}
          setError={() => false}
          onChange={handlePublicationDateChange}
        />
        <DateRange
          label="Issue Date"
          startDate={issueDateFilter.startDate ? new Date(issueDateFilter.startDate) : null}
          endDate={issueDateFilter.endDate ? new Date(issueDateFilter.endDate) : null}
          relativeRange={issueDateFilter.relativeRange ? issueDateFilter.relativeRange : null}
          resetInput={numberOfResets}
          error={false}
          setError={() => false}
          onChange={handleIssueDateChange}
        />
        <DateRange
          label="Maturity Date"
          startDate={maturityDateFilter.startDate ? new Date(maturityDateFilter.startDate) : null}
          endDate={maturityDateFilter.endDate ? new Date(maturityDateFilter.endDate) : null}
          relativeRange={maturityDateFilter.relativeRange ? maturityDateFilter.relativeRange : null}
          resetInput={numberOfResets}
          error={false}
          setError={() => false}
          onChange={handleMaturityDateChange}
        />
        <div className="separator" />
        <button className="reset-button" onClick={resetFilters} type="button">
          <Icon name={ICONS.Clear} width={12} height={12} /> Reset
        </button>
      </div>
    );
  };

  const renderCompany = (issuer, tags) => {
    const companyTag = tags.find((tag) => tag.startsWith('company/'));

    if (companyTag) {
      const companyId = companyTag.replace('company/', '');
      return <NavLink to={`/companies/${companyId}`}>{issuer}</NavLink>;
    } else {
      return <span>{issuer}</span>;
    }
  };

  const renderIssueName = (id, issue_name) => {
    return <NavLink to={`/articles/${id}`}>{issue_name}</NavLink>;
  };

  const renderDocumentName = (docType, docName, url) => {
    if (docType === 'Research Report') {
      return <span></span>;
    } else {
      return (
        <a target="_blank" href={url}>
          <i className="file alternate outline icon"></i> {docName}
        </a>
      );
    }
  };

  const renderDocument = ({ docName, docType, docUrl, file }) => {
    if (docUrl) {
      return renderDocumentName(docType, docName, docUrl);
    } else if (file) {
      const splits = file.split('/');
      const url = splits
        .map((i, index) => {
          if (index === splits.length - 1) {
            return encodeURIComponent(i);
          } else {
            return i;
          }
        })
        .join('/');
      return renderDocumentName(docType, docName, `/api/common/v1/attachments/${url}`);
    }

    return <span></span>;
  };

  const renderTotal = (total: number) => {
    const str = numeral(total).format('0,0');
    if (total === 10000) {
      return `more than ${str} records`;
    } else {
      return `${str} records`;
    }
  };

  const renderNoAccessMessage = () => {
    if ((filterError as any)?.response.status === 403) {
      return (
        <div className="no-access-ctx">
          <NoPermissions salesPersonEmail={profile?.assignedAccountManager?.email || DEFAULT_SUBSCRIPTIONS_EMAIL_ADDRESS} />
        </div>
      );
    }
  };

  const DocumentSearchTable = AGTable<any>;
  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);
  let columnDefs: ColDef<any>[];
  if (reportType !== 'reports') {
    columnDefs = useMemo(
      () => [
        {
          field: 'issuer',
          colId: 'issuer',
          valueGetter: (params) => {
            const data = params.data;
            return data;
          },
          cellRenderer: (params) => {
            const issuer = params.value.issuer;
            const tags = params.value.tags;

            return renderCompany(issuer, tags);
          },
        },
        {
          field: 'issue',
          colId: 'issue_name',
          valueGetter: (params) => {
            const data = params.data;
            return data;
          },
          cellRenderer: (params) => {
            const issueName = params.value.issue_name;
            const id = params.value.id;
            return renderIssueName(id, issueName);
          },
          width: 600,
        },
        {
          field: 'document',
          colId: 'docName',
          valueGetter: (params) => {
            const data = params.data;
            return data;
          },
          cellRenderer: (params) => {
            return renderDocument(params.value);
          },
        },
        {
          field: 'publication date',
          colId: 'publishDate',
          valueGetter: (params) => {
            const publishDate = params.data.publishDate;
            return publishDate;
          },
          cellRenderer: (params) => {
            return formatDate(params.value);
          },
        },
      ],
      [reportType]
    );
  } else {
    columnDefs = useMemo(
      () => [
        {
          field: 'issuer',
          colId: 'issuer',
          valueGetter: (params) => {
            const data = params.data;
            return data;
          },
          cellRenderer: (params) => {
            const issuer = params.value.issuer;
            const tags = params.value.tags;

            return renderCompany(issuer, tags);
          },
        },
        {
          field: 'issue',
          colId: 'issue_name',
          valueGetter: (params) => {
            const data = params.data;
            return data;
          },
          cellRenderer: (params) => {
            const issueName = params.value.issue_name;
            const id = params.value.id;
            return renderIssueName(id, issueName);
          },
          width: 600,
        },
        {
          field: 'publication date',
          colId: 'publishDate',
          valueGetter: (params) => {
            const publishDate = params.data.publishDate;
            return publishDate;
          },
          cellRenderer: (params) => {
            return formatDate(params.value);
          },
        },
      ],
      [reportType]
    );
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      gridApiRef.current?.setServerSideDatasource({
        getRows: async (params) => {
          const { request, success, fail } = params;
          const skip = request.startRow;
          const take = request.endRow && request.startRow !== undefined ? request.endRow - request.startRow : undefined;

          const sortField = request.sortModel[0]?.colId;
          const sortOrder = request.sortModel[0]?.sort ?? DEFAULT_SORT_ORDER;

          if (!skip) {
            gridApiRef.current?.showLoadingOverlay?.();
          }
          try {
            const { results, totalResults } = await queryClient.fetchQuery(
              queryKeys.fetchDocumentSearchDataWithoutSearchPhrase.query({ docType, sortField, sortOrder, searchPhrase, reportType, filterConfig, publicationDateFilter, issueDateFilter, maturityDateFilter, skip, take }),
              () => fetchDocumentSearchDataWithoutSearchPhrase(docType, sortField, sortOrder, searchPhrase, reportType, filterConfig, publicationDateFilter, issueDateFilter, maturityDateFilter, skip, take)
            );

            if (!skip) {
              setTotalResults(totalResults);
              setSortField(sortField);
              setSortOrder(sortOrder);

              if (results.length === 0) {
                gridApiRef.current?.showNoRowsOverlay?.();
              } else {
                gridApiRef.current?.hideOverlay?.();
              }
            }

            success({
              rowData: results,
              rowCount: Math.min(10000, totalResults),
            });
          } catch (e: any) {
            fail();
          }
        },
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [docType, searchPhrase, reportType, filterConfig, publicationDateFilter, issueDateFilter, maturityDateFilter, invalidate]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    event.api.showLoadingOverlay();
    event.api.sizeColumnsToFit();
    setInvalidate((cur) => !cur);
  }, []);

  return (
    <div className="cs-document-search-ctx">
      <div className="header-ctx">
        <div className="ctx">
          <div className="header">
            <div className="title">
              <span className="doc-type">{docType}</span> Precedent Search
            </div>
            <div className="separator" />
            <div className="nav-links">
              <NavLink to={DOCUMENT_SEARCH_ALL_ROUTE(docType) + `?${fetchUrlState()}`}>All</NavLink>
              <NavLink to={DOCUMENT_SEARCH_REPORTS_ROUTE(docType) + `?${fetchUrlState()}`}>Published Reports</NavLink>
              <NavLink to={DOCUMENT_SEARCH_SOURCE_DOCUMENTS_ROUTE(docType) + `?${fetchUrlState()}`}>Source Documents</NavLink>
            </div>
          </div>
        </div>
      </div>
      <div className="body-ctx">
        {filters && !filtersLoading && (
          <>
            <div className="key-phrase ui left icon input">
              <input placeholder="Search by key phrase" value={text} onChange={(e) => setText(e.target.value)} onBlur={updateSearchPhrase} onKeyUp={(e) => e.key === 'Enter' && updateSearchPhrase()} />
              <i className="search icon"></i>
            </div>
            {renderFilters()}
          </>
        )}
        {(isLoading || filtersLoading) && <Loader className="cs-loader" position="center" />}
        {!filterError && !filtersLoading && !data && !isLoading && (
          <div className="no-content-ctx">
            <div className="no-content">
              <Icon name={ICONS.NoContent} height={200} width={200} />
              <div className="text">Search for a document or report using a key phrase or filters above</div>
            </div>
          </div>
        )}
        {renderNoAccessMessage()}
        {data && !isLoading && (
          <>
            <div className="info">
              <div className="main">
                Search results for{' '}
                {searchPhrase && (
                  <span>
                    <em>{searchPhrase}</em> and
                  </span>
                )}{' '}
                applied filters
              </div>
              <div className="count">{renderTotal(data.pages[0]?.totalResults)}</div>
            </div>
            {searchPhrase && <DocumentSearchHighlights data={data} sort={sort} updateSort={updateSort} pageSize={PAGE_SIZE} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} />}
            {!searchPhrase && (
              <div style={{ height: '80vh' }}>
                <DocumentSearchTable
                  columnPersistenceKey={COLUMN_PERSISTENCE_KEY}
                  onGridReady={onGridReady}
                  rowModelType="serverSide"
                  columnDefs={columnDefs}
                  onSortChanged={onSortChanged}
                  suppressRowClickSelection
                  loadingOverlayComponent={Loader}
                  overlayNoRowsTemplate="No results found"
                  additionalDefaultColDef={additionalDefaultColDef}
                  suppressRowHoverHighlight={false}
                  cacheBlockSize={GRID_PAGE_SIZE}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DocumentSearch;
